import { startOfWeek, addWeeks, differenceInCalendarWeeks } from 'date-fns'
import {
  CreatePlanDataTypes,
  PlanDataTypes,
} from '../../OperationalPlanner.types'
import { SourceSiteDetails } from '../../WorkcentreModal/WorkcentreModal.types'
import { PLAN_ACCESS_TYPE } from 'Common/Constants/Constants'

// Pure utility functions - no React hooks, no side effects

export const getFormattedDate = (date: Date): string => {
  const mnth = date.getMonth() + 1
  const dd = date.getDate()
  const month = mnth < 10 ? `0${mnth}` : `${mnth}`
  const dateStr = dd < 10 ? `0${dd}` : `${dd}`
  return `${date.getFullYear()}-${month}-${dateStr}`
}

export const isValidStartWeek = (date: Date | undefined): boolean => {
  return date?.getDay() === 1 // Monday
}

export const calculateDateFromWeeks = (
  startDate: Date,
  weeks: number
): Date => {
  return addWeeks(startDate, weeks)
}

export const calculateWeeksFromDate = (
  startDate: Date,
  endDate: Date
): number => {
  const timeDiff = endDate.getTime() - startDate.getTime()
  return Math.ceil(timeDiff / (1000 * 3600 * 24 * 7))
}

export const getStartOfWeek = (date: Date = new Date()): Date => {
  return startOfWeek(date, { weekStartsOn: 1 })
}

export const createDefaultPlanData = (): CreatePlanDataTypes => ({
  planStartWeek: getStartOfWeek(),
  planDescription: '',
  planHorizon: 0,
  constrainingHorizon: 0,
  sourceSites: [],
  extractData: true,
  unconstrainedPlan: false,
  planAccessType: PLAN_ACCESS_TYPE.PRIVATE,
  planHorizonDate: getStartOfWeek(),
  constrainingHorizonDate: getStartOfWeek(),
})

export const isFormValid = (planData: CreatePlanDataTypes): boolean => {
  return !!(
    planData.planStartWeek &&
    planData.planHorizon > 0 &&
    planData.sourceSites.length > 0 &&
    Boolean(planData.planDescription.trim())
  )
}

export const canSelectSourceSites = (
  planData: CreatePlanDataTypes
): boolean => {
  return isValidStartWeek(planData.planStartWeek) && planData.planHorizon > 0
}

export const processSelectedSourceSites = (
  sourceSiteData: SourceSiteDetails[],
  constrainingHorizon = 4
) => {
  return sourceSiteData
    .filter((sourceSite) => sourceSite.isSelected)
    .map((sourceSite) => {
      const workcentres = sourceSite.siteWorkcentres
        .filter((workcentre) => workcentre.isSelected)
        .map((workcentre) => {
          return {
            ...workcentre,
            constrainingHorizon: constrainingHorizon,
            forwardFill: 0,
          }
        })
      return {
        siteKey: sourceSite.siteKey,
        siteCode: sourceSite.siteCode,
        siteLabel: sourceSite.siteLabel,
        isSelected: true,
        siteWorkcentres: workcentres,
      }
    })
}

export const preparePlanDataForSubmission = (
  planData: CreatePlanDataTypes,
  unconstrainedPlan: boolean
): PlanDataTypes => {
  const planStartWeek = planData.planStartWeek
  if (!planStartWeek) {
    throw new Error('Plan start week is required')
  }

  // Create a clean copy without date fields that shouldn't be sent to API
  const cleanPlanData = { ...planData }
  delete cleanPlanData.planHorizonDate
  delete cleanPlanData.constrainingHorizonDate

  return {
    ...cleanPlanData,
    planStartWeek: getFormattedDate(planStartWeek),
    unconstrainedPlan,
  }
}

export const prepareTemplateData = (
  planData: CreatePlanDataTypes,
  horizonsSelectionModeState: any
) => {
  // Prepare plan data for template (exclude runtime-specific fields)
  const templatePlanData: Partial<CreatePlanDataTypes> = { ...planData }
  delete templatePlanData.planStartWeek
  delete templatePlanData.planDescription

  return {
    ...templatePlanData,
    horizonAnchor: horizonsSelectionModeState,
  }
}

// Additional horizon calculation utilities
export const calculateHorizonDateFromWeeks = (
  startDate: Date,
  weeks: number
): Date => {
  return addWeeks(startDate, weeks)
}

export const calculateWeeksFromHorizonDate = (
  startDate: Date,
  horizonDate: Date
): number => {
  return differenceInCalendarWeeks(horizonDate, startDate)
}

// Constraining Horizons utilities
export interface WorkcentreTableRow {
  id: string // Unique identifier (siteKey + workcentreKey)
  siteLabel: string
  code: string // workcentre code
  constrainingHorizon: number // Default: 4
  forwardFill: number // Default: 0
  constrainingEndDate: Date // Calculated from planStartWeek + constrainingHorizon
  // Internal fields for reconstruction
  siteKey: string
  workcentreKey: string
  // Data Quality fields (optional - only populated when data quality is expanded)
  forecastDate?: string
  index?: number
  invalidBatch?: number
  invalidSkus?: number
  inventoryDate?: string
  missingChangeOvers?: number
  numberOfProducts?: number
  orphanProduction?: number
  orphanSkus?: number
  plannedProductionAmount?: number
  stockInTransit?: number
  workCentreCapacity?: number
  dataQualityWorkCentreKey?: string
}

// This method is invoked from edit settings workcentre tab.
export const flattenWorkcentresForEditSettings = (
  sourceSites: any[],
  planStartWeek: Date
): WorkcentreTableRow[] => {
  const tableRows: WorkcentreTableRow[] = []

  sourceSites.forEach((site) => {
    site.siteWorkcentres
      .filter((workcentre: any) => workcentre.isSelected)
      .forEach((workcentre: any) => {
        const constrainingHorizon = workcentre.constrainingHorizon || 4
        const forwardFill = workcentre.forwardFill || 0
        const constrainingEndDate = workcentre.constrainingEndDate
          ? new Date(workcentre.constrainingEndDate)
          : calculateHorizonDateFromWeeks(planStartWeek, constrainingHorizon)

        tableRows.push({
          id: `${site.siteKey}-${workcentre.key}`,
          siteLabel: site.siteLabel,
          code: workcentre.code,
          constrainingHorizon,
          forwardFill,
          constrainingEndDate,
          siteKey: site.siteKey,
          workcentreKey: workcentre.key,
        })
      })
  })

  // Sort by siteLabel then by code
  return tableRows.sort((a, b) => {
    if (a.siteLabel !== b.siteLabel) {
      return a.siteLabel.localeCompare(b.siteLabel)
    }
    return a.code.localeCompare(b.code)
  })
}

export const flattenWorkcentresForTable = (
  createPlanDataObject: CreatePlanDataTypes
): WorkcentreTableRow[] => {
  const tableRows: WorkcentreTableRow[] = []
  const {
    sourceSites,
    planStartWeek,
    constrainingHorizon: userDefinedConstrainedHorizon,
  } = createPlanDataObject

  // userDefinedConstrainedHorizon could be either from a variant or user manually entered in the UI befor.
  sourceSites.forEach((site) => {
    site.siteWorkcentres
      .filter((workcentre: any) => workcentre.isSelected)
      .forEach((workcentre: any) => {
        const constrainingHorizon =
          workcentre.constrainingHorizon || userDefinedConstrainedHorizon || 4
        const forwardFill = workcentre.forwardFill || 0
        const constrainingEndDate = workcentre.constrainingEndDate
          ? new Date(workcentre.constrainingEndDate)
          : calculateHorizonDateFromWeeks(
              planStartWeek as Date,
              constrainingHorizon
            )

        tableRows.push({
          id: `${site.siteKey}-${workcentre.key}`,
          siteLabel: site.siteLabel,
          code: workcentre.code,
          constrainingHorizon,
          forwardFill,
          constrainingEndDate,
          siteKey: String(site.siteKey),
          workcentreKey: workcentre.key,
        })
      })
  })

  // Sort by siteLabel then by code
  return tableRows.sort((a, b) => {
    if (a.siteLabel !== b.siteLabel) {
      return a.siteLabel.localeCompare(b.siteLabel)
    }
    return a.code.localeCompare(b.code)
  })
}

export const unflattenWorkcentresFromTable = (
  tableData: WorkcentreTableRow[],
  originalSourceSites: any[]
): any[] => {
  // Create a map for quick lookup of updated workcentre data
  const updatedWorkcentres = new Map<string, WorkcentreTableRow>()
  tableData.forEach((row) => {
    updatedWorkcentres.set(row.id, row)
  })

  // Update the original sourceSites structure
  return originalSourceSites.map((site) => ({
    ...site,
    siteWorkcentres: site.siteWorkcentres.map((workcentre: any) => {
      const updatedData = updatedWorkcentres.get(
        `${site.siteKey}-${workcentre.key}`
      )

      if (updatedData && workcentre.isSelected) {
        return {
          ...workcentre,
          constrainingHorizon: updatedData.constrainingHorizon,
          forwardFill: updatedData.forwardFill,
          constrainingEndDate: updatedData.constrainingEndDate,
        }
      }

      return workcentre
    }),
  }))
}

export const updateConstrainingEndDate = (
  planStartWeek: Date,
  constrainingHorizon: number
): Date => {
  return calculateHorizonDateFromWeeks(planStartWeek, constrainingHorizon)
}
